import { authContext } from "../auth/context";
import { useEffect, useState } from "react";
import { roleContext } from "./context";
import { useContext } from "react";

import { LoadingClass } from "../../roles/loading";
import { Incognit } from "../../roles/incognit";
import { Admin } from "../../roles/admin";
import { Cashier } from "../../roles/cashier";
import { SuperAdmin } from "../../roles/superadmin";
import { Accountant } from "../../roles/accountant";

import { deleteCookie, getCookie, setCookie } from "../../utils/cookies";
import { dispatchCustomEvent, listener } from "../../utils/dispatch";

import { getProfile, clearAllCachedProfiles } from "../../usecases/users-usecases";

import { getSubscription } from "../../usecases/subscriptions-usecase";

import { MdOutlineAccountBalanceWallet, MdOutlineInventory2, MdPostAdd, MdSell, MdShield, MdPeople, MdOutlineMiscellaneousServices } from "react-icons/md";
import { FaFileInvoice } from 'react-icons/fa';
const ROLE_IDS = {
    INCOGNIT: 0,
    SUPER_ADMIN: 1,
    ADMIN: 2,
    CASHIER: 3,
    ACCOUNTANT: 4
};

const ROLES = {
    [ROLE_IDS.INCOGNIT]: new Incognit(),
    [ROLE_IDS.SUPER_ADMIN]: new SuperAdmin(),
    [ROLE_IDS.ADMIN]: new Admin(),
    [ROLE_IDS.CASHIER]: new Cashier(),
    [ROLE_IDS.ACCOUNTANT]: new Accountant(),
};

const INITIAL_ADDONS = [
    { id: "pos", title: "Pos", icon: <MdSell />, link: "/pos" },
    { id: "compras", title: "Compras y Gastos", icon: <MdPostAdd />, link: "/compras" },
    { id: "accounts", title: "Cuentas Cobrar/Pagar", icon: <MdOutlineAccountBalanceWallet />, link: "/accounts-management" },
    { id: "invoice", title: "Facturas", icon: <FaFileInvoice />, link: "/invoice", disabled: true },
    { id: "stock", title: "Productos", icon: <MdOutlineInventory2 />, link: "/stock" },
    { id: "services", title: "Servicios", icon: <MdOutlineMiscellaneousServices />, link: "/services" },
    { id: "terceros", title: "CRM de Terceros", icon: <MdPeople />, link: "/terceros" },
    { id: "accounting", title: "Contabilidad", icon: <MdPostAdd />, link: "/accounting" },
];


export function RoleContext() {
    const { user } = useContext(authContext);
    const [roleRoutes, setRoleRoutes] = useState(new LoadingClass().getRoutes());
    const [profile, setProfile] = useState(null);
    const [suscripcion, setSuscripcion] = useState(undefined);
    const [isElectronicInvoiceEnable, setElectronicInvoiceEnable] = useState(false);

    const isInpersonated = !!getCookie('owner_user_id');
    const canCreate = profile?.role_id === ROLE_IDS.SUPER_ADMIN;

    const refreshSubscription = async () => {
        try {
            const res = await getSubscription();
            setSuscripcion(res.data);
            setElectronicInvoiceEnable(res.data.invoiceEnable);
        } catch (error) {
            dispatchCustomEvent('popup', { message: 'Error al actualizar suscripción' });
        }
    };

    useEffect(() => {
        const profileCleanup = listener("update-profile", (e) => setProfile(e.detail.data));
        const subscriptionCleanup = listener("update-subscription", refreshSubscription);

        return () => {
            profileCleanup();
            subscriptionCleanup();
        };
    }, []);

    useEffect(() => {
        const loadProfile = async () => {
            if (!user) {
                const cookie = getCookie('data');
                if (!cookie || cookie === 'null') {
                    setRoleRoutes(ROLES[ROLE_IDS.INCOGNIT].getRoutes());
                }
                return;
            }

            try {
                const currentCid = localStorage.getItem('cid');
                let res = await getProfile(user.uid, currentCid);
                
                // Fallback to cookie data if offline
                if (!res && user) {
                    res = user;
                }

                const role_id = res.colaborador ? res.colaborador.role_id : res.role_id;

                if (ROLES[role_id]) {
                    res.cid = localStorage.getItem('cid');
                    setProfile(res);
                    setRoleRoutes(ROLES[role_id].getRoutes());
                } else {
                    setRoleRoutes(ROLES[ROLE_IDS.INCOGNIT].getRoutes());
                }
            } catch (error) {
                console.error("Failed to load profile in RoleContext:", error);
                if (user) {
                    const role_id = user.colaborador ? user.colaborador.role_id : user.role_id;
                    if (ROLES[role_id]) {
                        setProfile(user);
                        setRoleRoutes(ROLES[role_id].getRoutes());
                        refreshSubscription();
                        return;
                    }
                }
                setRoleRoutes(ROLES[ROLE_IDS.INCOGNIT].getRoutes());
            }

            refreshSubscription();
        };

        loadProfile();
    }, [user]);

    const addons = profile ? (() => {
        const currentAddons = INITIAL_ADDONS.map(addon => {
            if (addon.id === "invoice") {
                return { ...addon, disabled: !isElectronicInvoiceEnable };
            }
            return addon;
        });

        if (profile.role_id === ROLE_IDS.SUPER_ADMIN || (profile.colaborador && profile.colaborador.role_id === ROLE_IDS.SUPER_ADMIN)) {
            if (!currentAddons.some(a => a.id === "colaboradores")) {
                currentAddons.push({
                    id: "colaboradores",
                    title: "Colaboradores",
                    icon: <MdShield />,
                    link: "/operations",
                });
            }
        }
        return currentAddons;
    })() : INITIAL_ADDONS;

    const impersonate = (owner_user_id) => {
        const intent = window.localStorage.getItem('pending_nav_intent');
        setCookie('owner_user_id', owner_user_id, 30);
        clearAllCachedProfiles();
        window.localStorage.clear();
        if (intent) window.localStorage.setItem('pending_nav_intent', intent);
        window.location.reload();
    };

    const unpersonate = () => {
        const intent = window.localStorage.getItem('pending_nav_intent');
        deleteCookie('owner_user_id');
        deleteCookie('cid');
        clearAllCachedProfiles();
        window.localStorage.clear();
        if (intent) window.localStorage.setItem('pending_nav_intent', intent);
        window.location.replace('/');
    };

    return (
        <roleContext.Provider value={{
            profile,
            suscripcion,
            canCreate,
            isElectronicInvoiceEnable,
            addons,
            impersonate,
            unpersonate,
            isInpersonated
        }}>
            {roleRoutes}
        </roleContext.Provider>
    );
}
